import type { MouseEvent } from 'react'
import { DeleteOutlined } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { Checkbox } from '../../../components/ui/Checkbox'
import { Chip } from '../../../components/ui/Chip'
import { cx } from '../../../components/ui/cx'
import type { MatchedPart, StagingRowSignal } from '../staging-types'
import {
  ANALYSIS_SIGNAL_REASON_LABEL,
  MATCH_TYPE_LABEL,
  MATCH_TYPE_TONE,
  stagingRowSignalTintClass,
} from '../staging-types'
import { MatchedSourcingPanel } from './MatchedSourcingPanel'

export interface MatchedPartRowProps {
  readonly part: MatchedPart
  readonly selected: boolean
  /** ADR-0130 — takes the raw click event so a shift-click can extend a
   * range selection across the unified identified+matched selection map. */
  readonly onToggleSelect: (event: MouseEvent<HTMLInputElement>) => void
  readonly expanded: boolean
  readonly onToggleExpand: () => void
  readonly onIgnore: () => void
  readonly onUndo: () => void
  readonly onSelectOption: (optionId: string) => void
  readonly onKeepUploaded: () => void
  /** UID-222 #8 — remove this matched part (real, `DELETE .../parts/:partId`). */
  readonly onRemove: () => void
  /** UID-255 (additive/optional) — this row's auto-quote-vs-manual signal,
   * looked up by `part.partId`. Absent ⇒ no tint/chip (defensive fallback). */
  readonly analysisSignal?: StagingRowSignal | undefined
}

/**
 * Matched-part row (S-E, UID-189, `Fictiv Platform.dc.html:2820-2867`):
 * uploaded name → matched catalog name + match-type badge, an "Ignored"
 * state, and an expandable sourcing-options panel with a "Keep my uploaded
 * part" row and per-option Select/Selected toggle.
 *
 * A `match-to-catalog`-produced match can come back with **zero** sourcing
 * options (staging-mock-data.ts's `part-5` fixture) — the prototype has no
 * markup for that state at all, so the empty caption below is this build's
 * own addition, not a mock-copy.
 */
export function MatchedPartRow({
  part,
  selected,
  onToggleSelect,
  expanded,
  onToggleExpand,
  onIgnore,
  onUndo,
  onSelectOption,
  onKeepUploaded,
  onRemove,
  analysisSignal,
}: MatchedPartRowProps) {
  const isIgnored = part.status === 'ignored'
  // UID-402 — shared helper. An ambiguous/optionless match no longer gets the
  // orange wash (orange now means "needs resolution" only); its Chip still says so.
  const signalTintClass = stagingRowSignalTintClass(analysisSignal)
  // UID-222 #13 — the sub-label under the matched catalog name shows the
  // SELECTED sourcing option's vendor/SKU, not the catalog name repeated.
  const selectedOption = part.sourcingOptions.find((option) => option.selected)
  const subLabel = selectedOption
    ? `${selectedOption.vendor} · ${selectedOption.sku}`
    : 'No vendor selected yet'
  // UID-222 #2/#6 — "BOM · line N" + "× N" qty, both additive/optional
  // server fields; render only once present rather than fabricating them.
  const quantity = part.quantity ?? 1

  return (
    <li className={cx('staging-matched-row', signalTintClass)}>
      <div className="staging-matched-summary">
        {/* UID-600 (bug follow-up) — migrated from a raw, unwrapped
            `<input type="checkbox">` to the shared `Checkbox` primitive,
            same as `IdentifiedPartRow`'s row checkbox (see that migration's
            comment for the paint mismatch this fixes, and `Checkbox`'s
            `onClick` prop doc comment for why the toggle stays wired through
            `onClick`, not `onChange` — shift-click range-select needs
            `event.shiftKey`, which only a raw `MouseEvent` carries). */}
        <Checkbox
          checked={selected}
          disabled={isIgnored}
          onClick={onToggleSelect}
          onChange={() => {}}
          label={<span className="sr-only">Select {part.name}</span>}
        />
        {part.thumbnailUrl ? (
          // Render as an <img>: React escapes the src attribute, so there's no
          // CSS `url()` context to break out of (a server-supplied thumbnailUrl
          // can't inject styles). Decorative — the row name carries the label.
          <img className="staging-row-thumb" src={part.thumbnailUrl} alt="" aria-hidden="true" />
        ) : (
          <span className="staging-row-thumb staging-row-thumb-placeholder" aria-hidden="true" />
        )}
        <div className="parts-row-name">
          <span className="parts-row-name-text" title={part.name}>
            {part.name}
          </span>
        </div>
        <span className="staging-matched-arrow" aria-hidden="true">
          →
        </span>
        <div className="parts-row-name">
          <span>{part.matchedCatalogItem}</span>
          <span className="caption-text">{subLabel}</span>
          <span className="caption-text staging-matched-meta">
            {part.bomLine !== undefined && <span>BOM · line {part.bomLine}</span>}
            <span>× {quantity}</span>
          </span>
        </div>
        {isIgnored ? (
          <Chip tone="warning">Ignored</Chip>
        ) : (
          <Chip tone={MATCH_TYPE_TONE[part.matchType]}>{MATCH_TYPE_LABEL[part.matchType]}</Chip>
        )}
        {analysisSignal && (
          <Chip
            tone={analysisSignal.disposition === 'auto-quote' ? 'success' : 'warning'}
            className="staging-row-signal-chip"
            title={ANALYSIS_SIGNAL_REASON_LABEL[analysisSignal.reason]}
          >
            {ANALYSIS_SIGNAL_REASON_LABEL[analysisSignal.reason]}
          </Chip>
        )}
        {/* ADR-0130 (Bug 4 item 5) — Qty is DISABLED on a matched row per the
            design (`Fictiv Platform.dc.html:3352`): quantity comes from the
            matched catalog item, not a free-form edit. Still labeled for a11y
            even though disabled — a screen reader user should still know what
            the (unusable) control represents. */}
        <label className="sr-only" htmlFor={`matched-qty-${part.partId}`}>
          Quantity for {part.name}
        </label>
        <input
          id={`matched-qty-${part.partId}`}
          inputMode="numeric"
          disabled
          title="Quantity is set by the matched catalog item"
          className="staging-qty-input staging-bom-qty-input"
          value={quantity}
          readOnly
        />
        <div className="staging-matched-actions">
          {isIgnored ? (
            <Button variant="outline" size="sm" onClick={onUndo}>
              Undo
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" aria-expanded={expanded} onClick={onToggleExpand}>
                {expanded ? 'Hide options' : 'Swap'}
              </Button>
              <Button variant="link" size="sm" onClick={onIgnore}>
                Ignore
              </Button>
            </>
          )}
          <button
            type="button"
            className="staging-row-remove"
            aria-label={`Remove ${part.name}`}
            onClick={onRemove}
          >
            <DeleteOutlined aria-hidden="true" />
          </button>
        </div>
      </div>
      {expanded && !isIgnored && (
        <MatchedSourcingPanel
          part={part}
          onSelectOption={onSelectOption}
          onKeepUploaded={onKeepUploaded}
        />
      )}
    </li>
  )
}
